import { useState, useEffect } from 'react'
import { useVerificationStart } from './_hooks/useVerificationStart'
import { useVerificationVerify } from './_hooks/useVerificationVerify'
import { useNavigate } from 'react-router-dom'

interface UsePhoneVerificationModalProps {
  onSuccess?: (response: any) => void
}

export const usePhoneVerificationModal = ({
  onSuccess,
}: UsePhoneVerificationModalProps = {}) => {
  const { verifyStart, isLoading: isStartLoading } = useVerificationStart()
  const { verifyVerify, isLoading: isVerifyLoading } = useVerificationVerify()

  const isInvestorRoute = location.pathname.includes('/investor/')

  const navigate = useNavigate()

  const [isVerifyingPhone, setIsVerifyingPhone] = useState(false)
  const [phone, setPhone] = useState('')
  const [requestId, setRequestId] = useState('')

  const [verificationCode, setVerificationCode] = useState('')
  const [wasResponseSuccess, setWasResponseSuccess] = useState(false)
  const [wasResponseError, setWasResponseError] = useState(false)

  const [waiting, setWaiting] = useState(true)
  const [secondsLeft, setSecondsLeft] = useState(60)

  useEffect(() => {
    if (!waiting || !isVerifyingPhone) return

    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          setWaiting(false)
          return 60
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [waiting, isVerifyingPhone])

  const startVerification = async (phoneNumber: string) => {
    setPhone(phoneNumber)
    setIsVerifyingPhone(true)
    setVerificationCode('')
    setWasResponseSuccess(false)
    setWasResponseError(false)
    setWaiting(true)
    setSecondsLeft(60)

    const response = await verifyStart({
      phoneNumber,
    })

    if (response?.success) {
      setRequestId(response.smsVerificationId)
      return response
    } else {
      setIsVerifyingPhone(false)
      navigate(
        isInvestorRoute
          ? '/investor/user-profile/change-phone'
          : '/vlastnik/user-profile/change-phone'
      )
    }
  }

  const stopVerification = async () => {
    setIsVerifyingPhone(false)
    setPhone('')
    setRequestId('')
    setVerificationCode('')
    setWasResponseSuccess(false)
    setWasResponseError(false)
    setWaiting(false)
  }

  const closeModalAfterSuccess = () => {
    setIsVerifyingPhone(false)
    setPhone('')
    setRequestId('')
    setVerificationCode('')
    setWasResponseSuccess(false)
    setWasResponseError(false)
    setWaiting(false)
  }

  const verifyPhone = async (code: string) => {
    if (!requestId) return

    const response: any = await verifyVerify(requestId, {
      code,
    })
    if (response.success) {
      setWasResponseSuccess(true)
      setTimeout(() => {
        onSuccess?.(response)
        closeModalAfterSuccess()
      }, 1000)
    } else {
      setWasResponseError(true)
    }
  }

  const handleCodeChange = (newCode: string) => {
    if (newCode.length === 0) {
      setWasResponseSuccess(false)
      setWasResponseError(false)
    }

    setVerificationCode(newCode)

    if (newCode.length === 4) {
      verifyPhone(newCode)
    }
  }

  const handleSendAgainClick = async () => {
    if (!phone) return

    let response: any = await verifyStart({
      phoneNumber: phone,
      timeout: 60,
      codeLength: 4,
    })

    if (response?.error) {
      response = await verifyStart({
        phoneNumber: phone,
        timeout: 60,
        codeLength: 4,
      })
    }
    if (response?.success) {
      setRequestId(response.smsVerificationId)
      setWaiting(true)
      setSecondsLeft(60)
      setVerificationCode('')
      setWasResponseSuccess(false)
      setWasResponseError(false)
    }
  }

  let verifyCodeInputState = ''
  if (isVerifyLoading) {
    verifyCodeInputState = 'loading'
  } else if (wasResponseSuccess) {
    verifyCodeInputState = 'success'
  } else if (wasResponseError) {
    verifyCodeInputState = 'error'
  }

  return {
    isVerifyingPhone,
    startVerification,
    stopVerification,
    phone,
    verificationCode,
    verifyCodeInputState,
    handleCodeChange,
    handleSendAgainClick,
    waiting,
    secondsLeft,
    isStartLoading,
    isVerifyLoading,
  }
}
